import { useEffect, useState } from 'react';
import { FiX, FiFilter } from 'react-icons/fi';
import { fetchAuditLogs } from '../api/auditService';

/* ── action pill colors ──────────────────── */
const ACTION_COLOR = {
  created:    { bg: '#dcfce7', color: '#15803d' },
  updated:    { bg: '#dbeafe', color: '#1d4ed8' },
  deleted:    { bg: '#fee2e2', color: '#dc2626' },
  assigned:   { bg: '#ede9fe', color: '#7c3aed' },
  reassigned: { bg: '#fef3c7', color: '#b45309' },
  converted:  { bg: '#d1fae5', color: '#065f46' },
  rejected:   { bg: '#fce7f3', color: '#9d174d' },
  locked:     { bg: '#f1f5f9', color: '#475569' },
  unlocked:   { bg: '#f0fdf4', color: '#166534' },
};

function actionStyle(action = '') {
  const key = action.toLowerCase().split('_')[0];
  return ACTION_COLOR[key] || { bg: '#f3f4f6', color: '#374151' };
}

function ActionPill({ action }) {
  const s = actionStyle(action);
  return (
    <span style={{
      background: s.bg, color: s.color,
      padding: '2px 10px', borderRadius: 999,
      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {action}
    </span>
  );
}

function EntityPill({ type }) {
  const MAP = {
    lead:  { bg: '#ede9fe', color: '#7c3aed' },
    agent: { bg: '#dbeafe', color: '#1d4ed8' },
    user:  { bg: '#fef3c7', color: '#b45309' },
  };
  const s = MAP[type?.toLowerCase()] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{
      background: s.bg, color: s.color,
      padding: '2px 10px', borderRadius: 999,
      fontSize: 12, fontWeight: 600,
    }}>
      {type}
    </span>
  );
}

/* ── changeset display ───────────────────── */
function ChangesetTable({ changes }) {
  if (!changes || typeof changes !== 'object') return <p className="report-empty">No changes recorded.</p>;
  const entries = Object.entries(changes);
  if (entries.length === 0) return <p className="report-empty">No changes recorded.</p>;
  return (
    <table className="audit-changeset-table">
      <thead>
        <tr><th>Field</th><th>Before</th><th>After</th></tr>
      </thead>
      <tbody>
        {entries.map(([field, diff]) => (
          <tr key={field}>
            <td className="audit-change-field">{field}</td>
            <td className="audit-change-before">{String(diff?.from ?? '—')}</td>
            <td className="audit-change-after">{String(diff?.to ?? '—')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── detail drawer ───────────────────────── */
function AuditDrawer({ log, onClose }) {
  if (!log) return null;
  const ts = log.created_at ? new Date(log.created_at).toLocaleString('en-IN') : '—';
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="lead-drawer">
        <div className="drawer-header">
          <span>Event Detail</span>
          <button className="modal-close" onClick={onClose}><FiX /></button>
        </div>

        <div className="drawer-section">
          <div className="drawer-row">
            <span className="drawer-label">Action</span>
            <span className="drawer-value"><ActionPill action={log.action} /></span>
          </div>
          <div className="drawer-row">
            <span className="drawer-label">Entity</span>
            <span className="drawer-value"><EntityPill type={log.entity_type} /></span>
          </div>
          <div className="drawer-row">
            <span className="drawer-label">Entity ID</span>
            <span className="drawer-value mono-cell">{log.entity_id || '—'}</span>
          </div>
          <div className="drawer-row">
            <span className="drawer-label">Actor ID</span>
            <span className="drawer-value mono-cell">{log.actor_id || '—'}</span>
          </div>
          <div className="drawer-row">
            <span className="drawer-label">Actor Role</span>
            <span className="drawer-value">{log.actor_role || '—'}</span>
          </div>
          <div className="drawer-row">
            <span className="drawer-label">Timestamp</span>
            <span className="drawer-value">{ts}</span>
          </div>
          {log.reason && (
            <div className="drawer-row">
              <span className="drawer-label">Reason</span>
              <span className="drawer-value">{log.reason}</span>
            </div>
          )}
        </div>

        <div className="drawer-section">
          <p className="drawer-label" style={{ marginBottom: 8 }}>Changes</p>
          <ChangesetTable changes={log.changes} />
        </div>
      </aside>
    </>
  );
}

/* ── KPI strip ───────────────────────────── */
function AuditKpiStrip({ logs }) {
  const total     = logs.length;
  const actors    = new Set(logs.map((l) => l.actor_id).filter(Boolean)).size;
  const entities  = new Set(logs.map((l) => l.entity_id).filter(Boolean)).size;
  const actions   = logs.reduce((acc, l) => { acc[l.action] = (acc[l.action] || 0) + 1; return acc; }, {});
  const topAction = Object.entries(actions).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';

  const items = [
    { label: 'Total Events',   value: total,     color: '#6366f1' },
    { label: 'Unique Actors',  value: actors,    color: '#06b6d4' },
    { label: 'Unique Entities',value: entities,  color: '#8b5cf6' },
    { label: 'Top Action',     value: topAction, color: '#f59e0b' },
  ];

  return (
    <div className="report-kpi-strip" style={{ marginBottom: 16 }}>
      {items.map((item) => (
        <div key={item.label} className="report-kpi-card" style={{ borderTopColor: item.color }}>
          <span className="report-kpi-label">{item.label}</span>
          <strong className="report-kpi-value" style={{ color: item.color, fontSize: 22 }}>{item.value}</strong>
        </div>
      ))}
    </div>
  );
}

/* ── Page ────────────────────────────────── */
function AuditLogPage() {
  const [logs, setLogs]         = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [page, setPage]         = useState(1);
  const [filters, setFilters]   = useState({ entity_type: '', action: '', actor_id: '', from: '', to: '' });
  const [applied, setApplied]   = useState({});
  const [activeLog, setActiveLog] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const params = { page, limit: 25, ...applied };
    fetchAuditLogs(params)
      .then((res) => {
        if (!mounted) return;
        setLogs(Array.isArray(res.data) ? res.data : []);
        setPagination(res.pagination || null);
      })
      .catch((err) => {
        if (mounted) setError(err?.response?.data?.message || 'Unable to load audit logs.');
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [page, applied]);

  const onChange = (e) => setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const applyFilters = () => {
    const p = {};
    if (filters.entity_type) p.entity_type = filters.entity_type;
    if (filters.action)      p.action      = filters.action;
    if (filters.actor_id)    p.actor_id    = filters.actor_id;
    if (filters.from) p.from = new Date(filters.from).toISOString();
    if (filters.to)   p.to   = new Date(filters.to + 'T23:59:59').toISOString();
    setApplied(p);
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ entity_type: '', action: '', actor_id: '', from: '', to: '' });
    setApplied({});
    setPage(1);
  };

  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <div>
      <div className="page-title-row">
        <h2>Audit Log</h2>
      </div>

      {/* Filter bar */}
      <div className="filter-bar" style={{ flexWrap: 'wrap', rowGap: 8 }}>
        <select name="entity_type" className="filter-select" value={filters.entity_type} onChange={onChange}>
          <option value="">All Entity Types</option>
          <option value="lead">Lead</option>
          <option value="agent">Agent</option>
          <option value="user">User</option>
        </select>
        <input name="action" className="filter-input" placeholder="Action contains…"
          style={{ width: 160 }} value={filters.action} onChange={onChange} />
        <input name="actor_id" className="filter-input" placeholder="Actor ID…"
          style={{ width: 140 }} value={filters.actor_id} onChange={onChange} />
        <label className="filter-date-label">
          From
          <input name="from" type="date" className="filter-input" value={filters.from} onChange={onChange} />
        </label>
        <label className="filter-date-label">
          To
          <input name="to" type="date" className="filter-input" value={filters.to} onChange={onChange} />
        </label>
        <button className="primary-btn" onClick={applyFilters}><FiFilter style={{ marginRight: 4 }} />Apply</button>
        {hasFilters && (
          <button className="ghost-btn" onClick={clearFilters}>Clear</button>
        )}
      </div>

      {/* KPI strip (built from current page data) */}
      {!loading && logs.length > 0 && <AuditKpiStrip logs={logs} />}

      {loading && <p className="report-loading">Loading audit logs…</p>}
      {error   && <p className="error-text">{error}</p>}

      {!loading && !error && (
        <>
          <div className="audit-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Action</th>
                  <th>Entity</th>
                  <th>Entity ID</th>
                  <th>Actor ID</th>
                  <th>Role</th>
                  <th>Reason</th>
                  <th>Changes</th>
                </tr>
              </thead>
              <tbody>
                {logs.length ? logs.map((log) => (
                  <tr key={log.id} className="audit-row" onClick={() => setActiveLog(log)} title="Click to view details">
                    <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                      {log.created_at ? new Date(log.created_at).toLocaleString('en-IN') : '—'}
                    </td>
                    <td><ActionPill action={log.action} /></td>
                    <td><EntityPill type={log.entity_type} /></td>
                    <td className="mono-cell">{log.entity_id || '—'}</td>
                    <td className="mono-cell">{log.actor_id || '—'}</td>
                    <td style={{ fontSize: 13 }}>{log.actor_role || '—'}</td>
                    <td style={{ fontSize: 13, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.reason || '—'}
                    </td>
                    <td>
                      {log.changes && Object.keys(log.changes).length > 0 ? (
                        <span className="audit-changes-count">
                          {Object.keys(log.changes).length} field{Object.keys(log.changes).length > 1 ? 's' : ''}
                        </span>
                      ) : <span style={{ color: '#9ca3af', fontSize: 13 }}>—</span>}
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="8" style={{ textAlign: 'center', color: '#9ca3af', padding: 24 }}>No audit logs found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 1 && (
            <div className="pagination-row">
              <button className="ghost-btn" disabled={!pagination.has_prev_page} onClick={() => setPage((p) => p - 1)}>
                ← Previous
              </button>
              <span>Page {pagination.current_page} of {pagination.total_pages} ({pagination.total_items} total)</span>
              <button className="ghost-btn" disabled={!pagination.has_next_page} onClick={() => setPage((p) => p + 1)}>
                Next →
              </button>
            </div>
          )}
        </>
      )}

      <AuditDrawer log={activeLog} onClose={() => setActiveLog(null)} />
    </div>
  );
}

export default AuditLogPage;

